import { h, JSX } from "@stencil/core";
import { Account, TLink } from "@phn/entities/content";
import { shopStateStore } from "@phn/state/shop-state-store";
import { isShopAvailable } from "@phn/utility/helper/shop-helper";
import { navContentStore } from "@phn/state/nav-content-store";
import { getAccountRoutingKey } from "@phn/entities/routing-key";
import { EventActions } from "@phn/utility/constants";
import { finderStateStore } from "@phn/state/finder-state-store";
import { navStateStore } from "@phn/state/nav-state-store";
import { IsLoggedIn } from "@phn/utility/helper";

export default function Links({
  content,
  onLogoutClick,
  showSkeleton,
}: {
  content: Account;
  showSkeleton: boolean;
  onLogoutClick: () => void;
}): JSX.Element {
  const { loggedInState, unreadMessagesCount, app, locale } =
    navStateStore.state;
  const isLoggedIn = IsLoggedIn(loggedInState) || false;

  const {
    links,
    messages,
    savedSearches,
    savedVehicles,
    onlineOrderHistory,
    connect,
  } = content;
  const { savedSearchesCount, savedVehiclesCount } = finderStateStore.state;
  const { wishlistItemsNumber } = shopStateStore.state.shopInformation;
  const { shop } = navContentStore.state;
  const fixedLinkElements: JSX.Element[] = [];
  const overviewId = "mainmenu.account.overview";

  // Need to re-arrange overview from the variable links array to the first position, should actually be a fixed item in content
  const overview: TLink = links.find((link) => link.id === overviewId);

  if (overview) {
    fixedLinkElements.push(MenuItemLink({ content: overview, showSkeleton }));
  }

  if (messages) {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: messages,
        count: unreadMessagesCount,
        showSkeleton,
      }),
    );
  }

  if (savedSearches && savedSearches.text !== "") {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: savedSearches,
        count: savedSearchesCount,
        showSkeleton,
      }),
    );
  }

  if (savedVehicles && savedVehicles.text !== "") {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: savedVehicles,
        count: savedVehiclesCount,
        showSkeleton,
      }),
    );
  }

  if (connect) {
    fixedLinkElements.push(MenuItemLink({ content: connect, showSkeleton }));
  }

  if (isShopAvailable(shop, locale, app) && shop?.wishlist) {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: shop.wishlist,
        count: wishlistItemsNumber,
        showSkeleton,
      }),
    );
  }

  if (onlineOrderHistory && onlineOrderHistory.text !== "") {
    fixedLinkElements.push(
      MenuItemLink({ content: onlineOrderHistory, showSkeleton }),
    );
  }

  // Everything below onlineOrderHistory is variable on the content side and can be re-arranged there
  const variableLinkElements: JSX.Element[] = links
    .filter((link: TLink) => link.id !== overviewId)
    .map<JSX.Element>((link: TLink) =>
      MenuItemLink({ content: link, showSkeleton }),
    );

  if (isLoggedIn) {
    variableLinkElements.push(
      LogoutLink({ content, onLogoutClick, showSkeleton }),
    );
  }

  const accountLinks = [...fixedLinkElements, ...variableLinkElements];
  return (
    <div class="account-links">
      {showSkeleton ? (
        <div class="skeleton-menu">
          {accountLinks.map(() => (
            <div class="skeleton-menu-item" />
          ))}
        </div>
      ) : null}
      <div style={{ display: showSkeleton ? "none" : "", width: "100%" }}>
        <div
          class={`${isLoggedIn ? "logged-in" : "logged-out"} account-links-container`}
        >
          {accountLinks}
        </div>
      </div>
    </div>
  );
}

function MenuItemCountLink({
  content,
  count,
  showSkeleton,
}: {
  content: TLink | null;
  count: number;
  showSkeleton: boolean;
}): JSX.Element | null {
  return content ? (
    <div class="account-link-item" key={content.id}>
      <phn-nd-menu-item
        elementId={getAccountRoutingKey(content.id)}
        class={`${showSkeleton ? "skeleton" : ""}`}
        analyticsId={content.id}
        text={content.text}
        link={content.link}
        datalayerEventAction={EventActions.NAVIGATION_LINK_CLICK}
      >
        <MenuItemCountIndicator count={count} />
      </phn-nd-menu-item>
    </div>
  ) : null;
}

function LogoutLink({
  content,
  onLogoutClick,
  showSkeleton,
}: {
  content: Account;
  showSkeleton: boolean;
  onLogoutClick: () => void;
}): JSX.Element {
  const { logout } = content;

  return (
    <div class="account-link-item" key={logout.id}>
      <phn-nd-menu-item
        elementId={getAccountRoutingKey(logout.id)}
        class={`account-menu-item logout ${showSkeleton ? "skeleton" : ""}`}
        analyticsId={logout.id}
        text={logout.text}
        onClick={onLogoutClick}
        datalayerEventAction={EventActions.NAVIGATION_LINK_CLICK}
      />
    </div>
  );
}

function MenuItemLink({
  content,
  showSkeleton,
}: {
  content: TLink | null;
  showSkeleton: boolean;
}): JSX.Element | null {
  return content ? (
    <div class="account-link-item" key={content.id}>
      <phn-nd-menu-item
        elementId={getAccountRoutingKey(content.id)}
        class={`${showSkeleton ? "skeleton" : ""}`}
        analyticsId={content.id}
        text={content.text}
        link={content.link}
        datalayerEventAction={EventActions.NAVIGATION_LINK_CLICK}
      />
    </div>
  ) : null;
}

function MenuItemCountIndicator({
  count,
}: {
  count: number;
}): JSX.Element | null {
  if (count <= 0) return null;

  return (
    <phn-p-tag
      slot="suffix"
      class="menu-item-count"
      color="notification-info-soft"
    >
      {count}
    </phn-p-tag>
  );
}
